import styled from '@emotion/styled/macro'
import colors from '@sportninja/common/constants/appColors'
import { Modal } from 'antd'
import React from 'react'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import WarningIcon from 'src/components/icons/WarningIcon'

interface HockeyCanadaWarningModalProps {
  /** Whether the modal is visible */
  open: boolean
  /** Whether the enable request is in flight */
  isUpdating: boolean
  /** Confirms enabling the integration */
  onConfirm: () => void
  /** Dismisses the modal without enabling */
  onCancel: () => void
}

// `Text` takes its overrides as a plain string rather than a styled component,
// and already applies the tenant-aware font per variant — so these only carry
// the size/alignment the design calls for.
const titleStyle = `
  font-size: 36px;
  line-height: 36px;
  text-align: center;
`

const actionLabelStyle = `
  font-size: 20px;
  line-height: 24px;
`

const bodyStyle = `
  font-size: 16px;
  line-height: 23.2px;
`

/** antd renders the mask/wrapper outside this subtree, so they're set via the
 *  `styles` prop rather than styled components. */
const MODAL_STYLES = {
  content: { backgroundColor: colors.SECONDARY_300 },
  wrapper: { zIndex: 10002 },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10001,
  },
}

/**
 * Cost-confirmation modal shown before enabling the Hockey Canada integration.
 */
const HockeyCanadaWarningModal: React.FC<HockeyCanadaWarningModalProps> = ({
  open,
  isUpdating,
  onConfirm,
  onCancel,
}) => {
  return (
    <StyledModal
      width={480}
      open={open}
      onCancel={onCancel}
      centered
      maskClosable={false}
      footer={null}
      styles={MODAL_STYLES}
    >
      <Content>
        <WarningIcon size={48} color={colors.PRIMARY} />

        <Text
          variant='heading1'
          weight='semiBold'
          color={colors.WHITE}
          style={titleStyle}
        >
          {capitalizeWords('Enable Hockey Canada Integration')}
        </Text>

        <BodyCopy>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.WHITE}
            style={bodyStyle}
          >
            This will enable Hockey Canada member validation during player
            registration. Please note that each validation check incurs a cost.
            Are you sure you want to continue?
          </Text>
        </BodyCopy>

        <Divider />

        <Actions>
          <ConfirmButton
            type='button'
            disabled={isUpdating}
            onClick={onConfirm}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={actionLabelStyle}
            >
              {isUpdating ? 'ENABLING...' : 'ENABLE INTEGRATION'}
            </Text>
          </ConfirmButton>

          <CancelButton type='button' disabled={isUpdating} onClick={onCancel}>
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={actionLabelStyle}
            >
              CANCEL
            </Text>
          </CancelButton>
        </Actions>
      </Content>
    </StyledModal>
  )
}

export default HockeyCanadaWarningModal

// ---- styled ----

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    background: ${colors.SECONDARY_300};
    border: 1px solid ${colors.SECONDARY_100};
  }
  .ant-modal-header {
    display: none;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  position: relative;
`

const BodyCopy = styled.div`
  align-self: stretch;
  text-align: center;
`

const Divider = styled.div`
  align-self: stretch;
  height: 1px;
  background: ${colors.SECONDARY_100};
`

const Actions = styled.div`
  align-self: stretch;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  display: flex;
  flex-direction: column;
`

const ActionButton = styled.button`
  width: 100%;
  padding: 8px 24px;
  border-radius: 8px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  display: flex;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ConfirmButton = styled(ActionButton)`
  background: ${colors.PRIMARY};
  border: 2px solid ${colors.PRIMARY};

  &:hover:not(:disabled) {
    opacity: 0.8;
  }
`

const CancelButton = styled(ActionButton)`
  border: 2px solid ${colors.WHITE};
  background: transparent;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
  }
`
